//
import { Component } from "react"
import { connect } from "react-redux"
import PropTypes from "prop-types"
import { fromJS } from "immutable"

import { genericFilterGroup } from "shared/search/containers/FilterGroup/FilterGroups"

import {
    createTransactionAmountFilter,
    SupporterPacEligibleFilter,
    SupporterPacHybridEligibleFilter,
    SupporterPacEligibilityFilter,
    SupporterPacConditionalGivingLevel,
    SupporterAssociatedWithPacFilter,
    SupporterHasSelectedCharityFilter,
    SupporterSelectedCharityNameFilter,
    createSupporterSelectedCharityDateFilter,
} from "shared/search/containers/Filter/Filters"

import { createPacClassicPacTransactionAmountFilter } from "shared/search/containers/Filter/PacClassicPacTransactionFilters"

// selectors
import { selectHasMultipleBankAccounts, selectLedgerSettingsListIds } from "shared/pac/selectors"

// helpers
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { checkHasPermission, hasAnyPermission } from "shared/imports/permissionFunctions"

/**
 * Generate the relevant Supporter PAC Eligible filter(s) using the FilterKeyMap
 * @param {Object} ledgerSettingsListIds - List of Ledger Settings ids for the org.
 * @param {Object} filterKeyMap - Mapping of the uniqueFilterId and it's associated filterKey
 * @return {Array} - An array of SupporterPACEligible filter objects.
 */
const parseAndGenerateSupporterPACEligibleFilters = ({
    ledgerSettingsListIds,
    hasMultipleBankAccounts,
    filterKeyMap,
}) =>
    Object.entries(filterKeyMap)
        .map(([uniqueFilterId]) => {
            const isPacHybrid = hasAnyPermission([DjangIO.app.models.PermissionType.pac_hybrid])
            if (isPacHybrid && uniqueFilterId === "supporterPacHybridEligibleFilter") {
                return SupporterPacHybridEligibleFilter
            } else if (uniqueFilterId === "supporterPacEligibilityFilter" && ledgerSettingsListIds.length > 1) {
                // returns a LedgerSettings select filter
                return SupporterPacEligibilityFilter
            } else if (uniqueFilterId === "supporterPacEligibleToggleFilter" && ledgerSettingsListIds.length <= 1) {
                // returns a True/False filter that determines whether to filter for the single ledger settings.
                return SupporterPacEligibleFilter(ledgerSettingsListIds[0])
            }
            if (uniqueFilterId === "supporterTransactionAmountFilter") {
                return createTransactionAmountFilter({
                    hasMultipleBankAccounts,
                    quorumDataType: DjangIO.app.models.QuorumDataType.supporter,
                })
            }
            if (uniqueFilterId === "supporterPacClassicPacTransactionAmountFilter") {
                return createPacClassicPacTransactionAmountFilter({
                    quorumDataType: DjangIO.app.models.QuorumDataType.supporter,
                })
            }
            if (uniqueFilterId === "supporterPacConditionalGivingLevel") {
                return SupporterPacConditionalGivingLevel
            }
            if (uniqueFilterId === "supporterHasSelectedCharityFilter") {
                return SupporterHasSelectedCharityFilter
            }
            if (uniqueFilterId === "supporterSelectedCharityNameFilter") {
                return SupporterSelectedCharityNameFilter
            }
            if (uniqueFilterId === "supporterAssociatedWithPacFilter") {
                return SupporterAssociatedWithPacFilter
            }
            if (uniqueFilterId === "supporterSelectedCharityDateFilter") {
                return createSupporterSelectedCharityDateFilter({
                    uniqueFilterId: "supporterSelectedCharityDate",
                })
            }

            console.warn("parseAndGenerateSupporterPACEligibleFilters received invalid uniqueFilterId")
            return undefined
        })
        .filter((item) => Boolean(item))

export const createSupporterPACFilterGroup = (uniqueFilterGroupId) => {
    const mapStateToProps = (state, props) => ({
        hasMultipleBankAccounts: selectHasMultipleBankAccounts,
        ledgerSettingsIds: selectLedgerSettingsListIds(state, props),
    })

    class UnconnectedSupporterPACFilterGroup extends Component {
        shouldComponentUpdate(nextProps) {
            return (
                !fromJS(nextProps.ledgerSettingsIds).equals(fromJS(this.props.ledgerSettingsIds)) ||
                nextProps.searchFilterTerm !== this.props.searchFilterTerm
            )
        }

        render() {
            const canViewDisbursementsOrReceipts =
                !isFeatureEnabled("ff_pac_permissions_one") ||
                Userdata.can_view_pac_receipts ||
                Userdata.can_view_pac_disbursements
            const canViewBothDisbursementsAndReceipts =
                !isFeatureEnabled("ff_pac_permissions_one") ||
                (Userdata.can_view_pac_receipts && Userdata.can_view_pac_disbursements)

            const isPacHybrid = hasAnyPermission([DjangIO.app.models.PermissionType.pac_hybrid])

            let pacEligibleFilter =
                this.props.ledgerSettingsIds.length > 1
                    ? [SupporterPacEligibilityFilter]
                    : [SupporterPacEligibleFilter(this.props.ledgerSettingsIds)]

            pacEligibleFilter = isPacHybrid ? [SupporterPacHybridEligibleFilter] : pacEligibleFilter
            const restrictedGRPagesFilter = isFeatureEnabled("ff_restricted_gr_pages") ? pacEligibleFilter : []

            // Check for PAC 2.0 permissions
            // BACKWARDS COMPATIBILITY: This logic only applies when ff_permissions_for_pac_2 is enabled
            // When disabled, the component falls through to legacy behavior (lines 142+)
            // When ff_permissions_for_pac_2 is enabled, ALL PAC 2.0 users follow the same rule:
            // Additional filters are only visible if user has can_view_pac_2_receipts permission
            // Otherwise, only PAC Eligible toggle and PAC selector filters are shown
            const isPac2User = isFeatureEnabled("ff_permissions_for_pac_2") && Userdata.can_access_pac_2_data

            const getFilters = () => {
                // PAC 2.0 Users: Show only PAC Eligible toggle and PAC selector unless they have receipts permission
                // This implements the requirement from QPE-2920 to limit PAC Activity filter options
                // for PAC 2.0 users without can_view_pac_2_receipts permission
                if (isPac2User && !Userdata.can_view_pac_2_receipts) {
                    const showSupportersAssociedWithPacFilter = hasAnyPermission([
                        DjangIO.app.models.PermissionType.pac_hybrid,
                    ])
                    return [
                        ...(showSupportersAssociedWithPacFilter ? [SupporterAssociatedWithPacFilter] : []),
                        ...restrictedGRPagesFilter,
                    ]
                }

                // For now, if an organization has both QPACv1 and PAC Hybrid, show the supporter filters from QPACv1
                // This allows sales demos to proceed
                const showQPACv1SupporterFilters = checkHasPermission([DjangIO.app.models.PermissionType.qpac_v1])
                const showSupportersAssociedWithPacFilter = hasAnyPermission([
                    DjangIO.app.models.PermissionType.pac_hybrid,
                ])
                const showPacHybridSupporterFilters =
                    checkHasPermission([DjangIO.app.models.PermissionType.pac_hybrid]) && !showQPACv1SupporterFilters

                if (!showPacHybridSupporterFilters && !canViewDisbursementsOrReceipts) {
                    return restrictedGRPagesFilter
                }

                return [
                    ...(showSupportersAssociedWithPacFilter ? [SupporterAssociatedWithPacFilter] : []),
                    ...restrictedGRPagesFilter,
                    ...(showQPACv1SupporterFilters
                        ? [
                              createTransactionAmountFilter({
                                  hasMultipleBankAccounts: this.props.hasMultipleBankAccounts,
                                  quorumDataType: DjangIO.app.models.QuorumDataType.supporter,
                                  uniqueFilterId: uniqueFilterGroupId,
                              }),
                          ]
                        : []),
                    ...(showPacHybridSupporterFilters
                        ? [
                              createPacClassicPacTransactionAmountFilter({
                                  quorumDataType: DjangIO.app.models.QuorumDataType.supporter,
                              }),
                          ]
                        : []),
                    ...(canViewBothDisbursementsAndReceipts ? [SupporterPacConditionalGivingLevel] : []),
                    ...(isFeatureEnabled("ff_pac_charitable_match_search") && Userdata.Permissions.qp_pac_match
                        ? [
                              SupporterHasSelectedCharityFilter,
                              SupporterSelectedCharityNameFilter,
                              createSupporterSelectedCharityDateFilter({
                                  uniqueFilterId: "supporterSelectedCharityDate",
                              }),
                          ]
                        : []),
                ]
            }

            return genericFilterGroup({
                ...this.props,
                uniqueFilterGroupId,
                filters: getFilters(),
                featureFlags: ["ff_pac_charitable_match_search"],
                displayLabel: "PAC Activity",
                permissions: [],
                helpText: "Filter contacts by PAC transactions.",
            })
        }
    }

    UnconnectedSupporterPACFilterGroup.propTypes = {
        hasMultipleBankAccounts: PropTypes.bool.isRequired,
        ledgerSettingsIds: PropTypes.array.isRequired,
        uniqueFilterGroupId: PropTypes.string.isRequired,
        uniqueFlvId: PropTypes.string.isRequired,
        // selected from the search => props.uniqueFlvId slice of the store
        searchFilterTerm: PropTypes.string,
    }

    const connectedFilterGroup = connect(mapStateToProps, null)(UnconnectedSupporterPACFilterGroup)
    Object.assign(connectedFilterGroup, {
        defaultProps: {
            uniqueFilterGroupId,
            filterKeyMap: {
                supporterPacEligibilityFilter: "for_pac_eligibilities",
                supporterPacEligibleToggleFilter: "",
                supporterPacHybridEligibleFilter: "for_pac_hybrid_eligibilities",
                supporterTransactionAmountFilter: "transactions__amount",
                supporterPacClassicPacTransactionAmountFilter: "associated_transactions",
                supporterPacConditionalGivingLevel: "pac_conditional_giving_level__iexact",
                supporterHasSelectedCharityFilter: "has_selected_charity",
                supporterAssociatedWithPacFilter: "for_pac_supporters_associated_with",
                supporterSelectedCharityNameFilter: "selected_charity_name",
                supporterSelectedCharityDateFilter: "selected_charity_date",
            },
            isConnectedFilterGroup: true,
            parseFunction: parseAndGenerateSupporterPACEligibleFilters,
            filters: [],
        },
    })

    return connectedFilterGroup
}
